/**
 * 全局搜索 —— 妈妈使用频率最高的入口，优先级高于任何导航项。
 *
 * 输入即搜、300ms 防抖、结果保留上一次不清空（避免闪烁）。
 * 找不到时把失败转成下一步动作：「用这个号码新建顾客」。
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { Loader2, Search, UserPlus, X } from 'lucide-react'
import { api, type SearchItem } from '../lib/api'
import { Avatar, Badge, Button, EmptyState, cx } from '../components/ui'
import { relativeDay, rm } from '../lib/format'
import { NewCustomerDrawer } from '../features/NewCustomerDrawer'

export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [newOpen, setNewOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30)
    else setQ('')
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const results = useQuery({
    queryKey: ['search', debounced],
    queryFn: () =>
      api.get<{ items: SearchItem[] }>(
        `/api/customers/search?q=${encodeURIComponent(debounced)}&limit=12`,
      ),
    // 空关键词返回最近有动静的顾客 —— 打开就有东西看，不是一片空白
    enabled: open,
    // 保留上一次结果，切换关键词时不闪空
    placeholderData: (prev) => prev,
  })

  if (!open) return null

  const items = results.data?.items ?? []
  const searching = debounced.trim().length >= 2
  const stale = q !== debounced || results.isFetching

  return createPortal(
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[8vh]">
        <div className="gb-anim-overlay absolute inset-0 bg-[rgb(28_25_23/0.4)]" onClick={onClose} />

        <div
          role="dialog"
          aria-modal="true"
          aria-label="搜索顾客"
          className="gb-anim-modal relative flex max-h-[76vh] w-full max-w-[560px] flex-col overflow-hidden rounded-lg bg-white shadow-lg"
        >
          <div className="flex shrink-0 items-center gap-3 border-b border-line px-4">
            <Search size={22} className="shrink-0 text-ink-3" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="电话后 4 位 / 姓名 / 顾客编号"
              className="h-16 flex-1 bg-transparent text-[19px] placeholder:text-ink-3 focus:outline-none"
            />
            {searching && stale && (
              <Loader2 size={20} className="shrink-0 animate-spin text-ink-3" />
            )}
            <button
              onClick={onClose}
              aria-label="关闭"
              className="flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-sunken"
            >
              <X size={22} />
            </button>
          </div>

          <div className={cx('flex-1 overflow-y-auto', stale && 'opacity-60')}>
            {items.length === 0 && !results.isFetching ? (
              <EmptyState
                title={searching ? `找不到「${debounced}」相关的顾客` : '还没有顾客'}
                action={
                  <Button
                    variant="primary"
                    size="lg"
                    icon={<UserPlus size={20} />}
                    onClick={() => setNewOpen(true)}
                  >
                    用这个新建顾客
                  </Button>
                }
              />
            ) : (
              <>
                {!searching && (
                  <p className="px-4 pt-3 text-[14px] text-ink-3">最近来店</p>
                )}
              <ul>
                {items.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => {
                        onClose()
                        navigate(`/customers/${c.id}`)
                      }}
                      className="flex w-full items-center gap-3 border-b border-line px-4 py-3.5 text-left transition-colors last:border-0 hover:bg-sunken"
                    >
                      <Avatar name={c.fullName} size={40} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-[17px] font-semibold">
                            <Highlight text={c.fullName} needle={debounced} />
                          </span>
                          {c.hasOutstanding && (
                            <Badge tone="warn">
                              {c.outstandingAmount === null ? '有欠款' : `欠 ${rm(c.outstandingAmount)}`}
                            </Badge>
                          )}
                        </span>
                        <span className="tnum mt-0.5 flex flex-wrap gap-x-3 text-[15px] text-ink-2">
                          <span>{c.phoneMasked}</span>
                          <span className="text-ink-3">
                            {c.lastVisitAt ? `上次到店 ${relativeDay(c.lastVisitAt)}` : '还没到过店'}
                          </span>
                          <span className="text-ink-3">{c.caseCount} 张工单</span>
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              </>
            )}
          </div>
        </div>
      </div>

      <NewCustomerDrawer
        open={newOpen}
        initialPhone={/^\d+$/.test(debounced) ? debounced : ''}
        initialName={/^\d+$/.test(debounced) ? '' : debounced}
        onClose={() => setNewOpen(false)}
        onCreated={(id) => {
          setNewOpen(false)
          onClose()
          navigate(`/customers/${id}`)
        }}
      />
    </>,
    document.body,
  )
}

/** 匹配片段高亮 —— 老板要能一眼确认搜到的是不是想找的人。 */
function Highlight({ text, needle }: { text: string; needle: string }) {
  const i = text.toLowerCase().indexOf(needle.toLowerCase())
  if (i < 0 || !needle) return <>{text}</>
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded-[3px] bg-brand-100 text-inherit">
        {text.slice(i, i + needle.length)}
      </mark>
      {text.slice(i + needle.length)}
    </>
  )
}
